// src/pages/AddressRegister.js
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { useFormInput } from "@/utils";
import { Confirm, Loading, Toast } from "@/components";
import { useGetAddressCode2Mutation, useGetAddressCodeQuery } from "@/redux/metaApi";
import { useGetMemberAreaDetailQuery, useUpdateMemberAreaMutation } from "@/redux/adminMemberApi";

export const AddressRegister = () => {
    const { id } = useParams(); // id는 이제 memberId를 의미
    const navigate = useNavigate();
    const location = useLocation();
    const { state } = location;

    const [isLoading, setIsLoading] = useState(false);

    const { formData, handleChange, setFormData } = useFormInput({
        mode: "UPDATE",
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        memberId: id || state?.memberId,
        selectedLocations: [],
        rentCorpAreas: state?.rentCorpAreas || [], // 전달받은 rentCorpAreas 저장
    });

    const { mode, toastMsg, confirmStatus, memberId, rentCorpAreas } = formData;

    // 1차 지역 데이터
    const { data: loc1, isLoading: isLoadingLoc1 } = useGetAddressCodeQuery({ take_1_step: true, take_2_step: false });
    // 2차 지역 호출을 위한 mutation hook
    const [getAddressCode2, { isLoading: isLoadingLoc2 }] = useGetAddressCode2Mutation();
    // 선택된 1차 지역 저장
    const [selectedLoc1, setSelectedLoc1] = useState(null);
    // 2차 지역 데이터
    const [loc2Data, setLoc2Data] = useState([]);

    // 활동지역 상세조회 - 회원 기준으로 변경
    const { data: areaDetailData, isLoading: isLoadingAreaDetail } = useGetMemberAreaDetailQuery(memberId, { skip: !memberId });

    // updateMemberArea mutation
    const [updateMemberArea] = useUpdateMemberAreaMutation();

    // 초기 선택 지역 설정 - 전달받은 rentCorpAreas 이용
    useEffect(() => {
        if (rentCorpAreas && rentCorpAreas.length > 0) {
            const selectedLocations = [];
            rentCorpAreas.forEach((area) => {
                area.childAddressCodes.forEach((child) => {
                    if (child.selected) {
                        selectedLocations.push({
                            id: child.id,
                            address1: area.name,
                            address2: child.name,
                            bCode: child.areaCode,
                        });
                    }
                });
            });

            if (selectedLocations.length > 0) {
                setFormData((prev) => ({ ...prev, selectedLocations }));
            }
        }
    }, [rentCorpAreas, setFormData]);

    // 2차 지역 체크박스 변경 처리
    const handleLoc2CheckboxChange = (option, checked) => {
        setFormData((prev) => {
            const prevSelected = prev.selectedLocations || [];
            let updated;
            if (checked) {
                updated = [...prevSelected, option];
            } else {
                updated = prevSelected.filter((o) => o.id !== option.id);
            }
            return { ...prev, selectedLocations: updated };
        });
    };

    // 1차 지역 버튼 클릭 시: 해당 지역의 자식 주소(2차 지역)를 불러온다.
    const handleLoc1Click = async (item) => {
        setSelectedLoc1(item);
        try {
            const response = await getAddressCode2({
                take_1_step: false,
                take_2_step: true,
                parent_b_code: item.areaCode,
            }).unwrap();
            setLoc2Data(response.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (loc1 && loc1.data) {
            handleLoc1Click(loc1.data[0]);
        }
    }, [loc1]);

    // API 응답 데이터를 처리하여 selectedLocations에 매핑
    useEffect(() => {
        if (areaDetailData?.data?.memberAreas && (!rentCorpAreas || rentCorpAreas.length === 0)) {
            const selectedLocations = [];
            areaDetailData.data.memberAreas.forEach((area) => {
                area.childAddressCodes.forEach((child) => {
                    if (child.selected) {
                        selectedLocations.push({
                            id: child.id,
                            address1: area.name,
                            address2: child.name,
                            bCode: child.areaCode,
                        });
                    }
                });
            });
            setFormData((prev) => ({ ...prev, selectedLocations }));
        }
    }, [areaDetailData, rentCorpAreas, setFormData]);

    useEffect(() => {
        if (id) {
            setFormData((prev) => ({ ...prev, memberId: id }));
        }
    }, [id]);

    // 저장 Confirm
    const handleSaveConfirm = async () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: { confirmPopupStatus: false },
        }));

        try {
            const requestBody = {
                addressCodes: formData.selectedLocations.map((loc) => ({
                    id: loc.id,
                })),
            };

            setIsLoading(true);

            await updateMemberArea({
                memberId: memberId,
                body: requestBody,
            }).unwrap();

            setIsLoading(false);
            setFormData((prev) => ({
                ...prev,
                toastMsg: "수정이 완료되었습니다.",
            }));
        } catch (error) {
            console.error(error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
            }));
        }
    };

    // 저장 버튼
    const handleSave = () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: "지역 정보를 수정하시겠습니까?",
                confirmDescription: "",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: handleSaveConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management-form container">
                <div className="form-container">
                    <div className="form-head">
                        <div className="head-tit">
                            <span className="form_name">회원 지역 설정</span>
                            <span className="form_desc"></span>
                        </div>
                        <div className="head-options">
                            <button type="button" className="btn btn-outline" onClick={() => navigate(-1)}>
                                이전으로
                            </button>
                            <button type="button" className="btn btn-save" onClick={handleSave}>
                                저장
                            </button>
                        </div>
                    </div>

                    <div className="form-content">
                        <div className="form-wrapper">
                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">지역 상세</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <AddressList
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            loc1={loc1}
                                            loc2Data={loc2Data}
                                            selectedLoc1={selectedLoc1}
                                            handleLoc1Click={handleLoc1Click}
                                            handleLoc2CheckboxChange={handleLoc2CheckboxChange}
                                            areaDetailData={areaDetailData}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm 팝업 */}
            {formData.confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={formData.confirmStatus.confirmTitle}
                    description={formData.confirmStatus.confirmDescription}
                    buttons={formData.confirmStatus.confirmButtons}
                />
            )}
        </>
    );
};

const AddressList = ({
    formData,
    handleChange,
    setFormData,
    loc1,
    loc2Data,
    selectedLoc1,
    handleLoc1Click,
    handleLoc2CheckboxChange,
    areaDetailData,
}) => {
    console.log(areaDetailData);

    // 전체 선택 체크박스 상태 계산
    const isAllChecked =
        loc2Data && loc2Data.length > 0 && loc2Data.every((option) => formData.selectedLocations.some((loc) => loc.id === option.id));

    // 전체 선택/해제 핸들러
    const handleSelectAll = (checked) => {
        if (checked) {
            // 모든 2차 지역 선택
            const allLocations = loc2Data.map((option) => ({
                id: option.id,
                address1: selectedLoc1.address1,
                address2: option.address2,
                bCode: option.areaCode,
            }));
            setFormData((prev) => ({
                ...prev,
                selectedLocations: [
                    ...prev.selectedLocations,
                    ...allLocations.filter((loc) => !prev.selectedLocations.some((existing) => existing.id === loc.id)),
                ],
            }));
        } else {
            // 현재 선택된 1차 지역의 2차 지역들만 해제
            setFormData((prev) => ({
                ...prev,
                selectedLocations: prev.selectedLocations.filter((loc) => !loc2Data.some((option) => option.id === loc.id)),
            }));
        }
    };

    return (
        <>
            {/* 1차 지역 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>1차 지역</span>
                </div>
                <div className="row-content">
                    <div className="location1-list">
                        {loc1 &&
                            loc1.data &&
                            loc1.data.map((item) => {
                                const matchingArea = areaDetailData?.data?.memberAreas?.find((area) => area.name === item.address1);
                                const isSelected = selectedLoc1 && selectedLoc1.id === item.id;
                                const isActive = matchingArea?.selected || false;
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        className={`btn-location1 ${isSelected ? "selected" : ""} ${isActive ? "active" : ""}`}
                                        onClick={() => handleLoc1Click(item)}
                                    >
                                        {item.address1}
                                    </button>
                                );
                            })}
                    </div>
                </div>
            </div>

            {/* 2차 지역 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>2차 지역</span>
                </div>
                <div className="row-content">
                    <div className="location2-list">
                        {/* 전체 선택 체크박스 */}
                        {loc2Data && loc2Data.length > 0 && (
                            <div className="checkbox select-all">
                                <input
                                    type="checkbox"
                                    id="select_all"
                                    checked={isAllChecked}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                />
                                <label htmlFor="select_all">
                                    <span>전체 선택</span>
                                </label>
                            </div>
                        )}
                        {loc2Data &&
                            loc2Data.map((option) => {
                                const isChecked = formData.selectedLocations.some((loc) => loc.id === option.id);
                                return (
                                    <div className="checkbox" key={option.id}>
                                        <input
                                            type="checkbox"
                                            id={`loc2_${option.id}`}
                                            checked={isChecked}
                                            onChange={(e) => handleLoc2CheckboxChange(option, e.target.checked)}
                                        />
                                        <label htmlFor={`loc2_${option.id}`}>
                                            <span>{option.address2}</span>
                                        </label>
                                    </div>
                                );
                            })}
                    </div>
                </div>
            </div>
        </>
    );
};
